import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Modal, ModalActions, ModalHeader, ModalSubmitButton, showModal} from '@edna/components';
import {Divider, SkeletonItem, Stack} from '@edna/components/primitives';

import {InlineLink, InlineLinkButton, Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {EWhatsappTrafficType, UNDEFINED_COUNTRY} from 'src/containers/TariffsCatalog';
import {useEdnaLinks} from 'src/hooks';
import {ExternalLinkIcon} from 'src/icons';
import {getCountryLabel} from 'src/utils';

import {TAvailableTariff} from '../../definitions';
import * as S from './style';
import {useCountriesPrices, useLicensePrices} from './usePrices';
import {usePricingLink} from './usePricingLink';

type TModalProps = {
  tariff: TAvailableTariff;
  closeModal: TEmptyFunction;
};

const TRAFFIC_TYPES = Object.values(EWhatsappTrafficType);

const WhatsAppPricingModal = React.memo<TModalProps>(({closeModal, tariff}) => {
  const {t} = useTranslation();

  const countriesPrices = useCountriesPrices(tariff);

  return (
    <Modal width="946px">
      <ModalHeader>{t('Tariffs:whatsappPricing.modalTitle')}</ModalHeader>
      <Stack>
        <S.CollapsibleSection
          isOpenDefault
          label={t('Tariffs:whatsappPricing.pricing', {currency: countriesPrices?.currency})}
          channelType={EChannelType.WHATSAPP}
        >
          <S.Tr noBorder>
            <S.Th>{t('Tariffs:whatsappPricing.country')}</S.Th>
            {TRAFFIC_TYPES.map((trafficType) => (
              <S.Th key={trafficType}>
                {t(`Tariffs:whatsappPricing.traffic.${trafficType}`)}
                {trafficType === EWhatsappTrafficType.SERVICE && (
                  <S.InfoDropdown
                    hasFilledHeadIcon
                    placementX="left"
                    content={t('Tariffs:whatsappPricing.serviceHint')}
                  />
                )}
              </S.Th>
            ))}
          </S.Tr>
          <S.ScrollContainer size="0">
            {countriesPrices?.prices.map((item, index) => (
              <S.Tr key={index}>
                <S.Td>
                  {item.country === UNDEFINED_COUNTRY
                    ? t('Country:other')
                    : getCountryLabel(t, item.country)}
                </S.Td>
                {TRAFFIC_TYPES.map((trafficType) => (
                  <S.Td key={trafficType}>{item.prices[trafficType]}</S.Td>
                ))}
              </S.Tr>
            ))}
          </S.ScrollContainer>
        </S.CollapsibleSection>
        <Divider />
        <Text secondary size="XS" topIndent="2">
          {t('Tariffs:pricing.vatHint')}
        </Text>
      </Stack>
      <ModalActions>
        <ModalSubmitButton onClick={closeModal} appearance="secondary">
          {t('Common:button.close')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

WhatsAppPricingModal.displayName = 'WhatsAppPricingModal';

const showWhatsAppPricingModal = (tariff: TModalProps['tariff']) =>
  showModal((closeModal) => <WhatsAppPricingModal closeModal={closeModal} tariff={tariff} />);

type TWhatsAppPricingProps = {
  subjectId?: number;
  tariff: TAvailableTariff;
};

const WhatsAppPricing = React.memo<TWhatsAppPricingProps>(({subjectId, tariff}) => {
  const {t} = useTranslation();
  const links = useEdnaLinks();

  const licensePrices = useLicensePrices(tariff);
  const pricingLink = usePricingLink({channelType: EChannelType.WHATSAPP, subjectId});

  if (!licensePrices || !tariff) {
    return (
      <S.LoadingContainer>
        <SkeletonItem height="50px" />
        <SkeletonItem height="50px" />
      </S.LoadingContainer>
    );
  }

  return (
    <S.Pricing>
      <Text weight={500}>
        {t('Tariffs:whatsappPricing.licensePrice')}
        <Text secondary size="S" topIndent="1">
          {t('Tariffs:whatsappPricing.licensePriceHint', {
            currency: licensePrices.currency,
            period: t(`Tariffs:licensePeriod.${licensePrices.period}`).toLowerCase(),
            mauCount: licensePrices.mauCount,
            licensePrice: licensePrices.licensePrice,
            amauPrice: licensePrices.amauPrice,
          })}
        </Text>
      </Text>
      <Text weight={500}>
        {t('Tariffs:whatsappPricing.detailPricing')}
        <Text secondary size="S" topIndent="1">
          <Trans i18nKey="Tariffs:whatsappPricing.detailPricingHint">
            {subjectId ? (
              <InlineLinkButton onClick={() => showWhatsAppPricingModal(tariff)} />
            ) : (
              <InlineLink href={pricingLink} />
            )}
            <InlineLink href={links.WA_TEMPLATE_MESSAGES} />
            <S.LinkIcon as={ExternalLinkIcon} size="12px" />
          </Trans>
        </Text>
      </Text>
      <Text weight={500}>
        {t('Tariffs:whatsappPricing.connectionFee')}
        <Text secondary size="S" topIndent="1">
          {t('Tariffs:whatsappPricing.connectionFeeHint', {
            currency: licensePrices.currency,
            connectionFee: licensePrices.connectionFee,
          })}
        </Text>
      </Text>
      <Text secondary size="XS">
        <Trans i18nKey="Tariffs:whatsappPricing.pricingHint" />
      </Text>
    </S.Pricing>
  );
});

WhatsAppPricingModal.displayName = 'WhatsAppPricingModal';

export {WhatsAppPricing};
